import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onForegroundMessage } from '../utils/notificationService';
import './ForegroundPushToast.css';

// When the tab is focused, FCM delivers messages to onMessage instead of
// the service worker — the browser will NOT raise a system notification.
// This component subscribes to those foreground deliveries and renders an
// in-app banner so the user actually sees something. Without it, sends
// look like silent failures even though FCM reports "delivered."

const AUTO_DISMISS_MS = 6000;

function ForegroundPushToast({ user }) {
  const [message, setMessage] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return undefined;

    let cancelled = false;
    let unsubscribe = () => {};

    onForegroundMessage((payload) => {
      const notification = payload?.notification || {};
      const data = payload?.data || {};
      const title = notification.title || data.title || 'Notification';
      const body = notification.body || data.body || '';
      // Prefer data.link (relative path the app routes to). fcmOptions.link
      // is the absolute URL FCM would have opened from the system banner.
      const link = data.link || payload?.fcmOptions?.link || null;
      setMessage({ title, body, link });
    }).then((unsub) => {
      if (cancelled) unsub();
      else unsubscribe = unsub;
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [user]);

  useEffect(() => {
    if (!message) return undefined;
    const t = setTimeout(() => setMessage(null), AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, [message]);

  const handleNavigate = useCallback(() => {
    const link = message?.link;
    setMessage(null);
    if (!link) return;
    try {
      const url = new URL(link, window.location.origin);
      if (url.origin === window.location.origin) {
        navigate(url.pathname + url.search + url.hash);
      } else {
        window.location.href = link;
      }
    } catch (_) {
      navigate(link);
    }
  }, [message, navigate]);

  if (!message) return null;

  return (
    <div
      className="fg-push-toast"
      role="alert"
      aria-live="polite"
      tabIndex={0}
      data-testid="foreground-push-toast"
      onClick={handleNavigate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleNavigate();
        }
      }}
    >
      <div className="fg-push-toast-body-wrap">
        <div className="fg-push-toast-title">{message.title}</div>
        {message.body && <div className="fg-push-toast-body">{message.body}</div>}
      </div>
      <button
        type="button"
        className="fg-push-toast-close"
        aria-label="Dismiss notification"
        data-testid="foreground-push-toast-close"
        onClick={(e) => {
          e.stopPropagation();
          setMessage(null);
        }}
      >
        ×
      </button>
    </div>
  );
}

export default ForegroundPushToast;
